/**
 * Header-role synonym dictionary + header normalizer. Data-driven on purpose:
 * roles map to a list of known header phrases (no hardcoded `if` chains in the
 * matcher), so coverage extends by editing DICTIONARY, not code. US-first per
 * M3 scope. The fuzzy matcher (columnMatch.ts) normalizes both the source
 * header and these phrases through `normalizeHeader` before comparing.
 */

import type { ColumnRole } from "./types";

/**
 * Normalize a raw header for comparison: lowercase, treat separators
 * (_ - / . and whitespace) as a single space, drop other punctuation, collapse
 * runs of space, trim. "Address_Line 1" -> "address line 1"; "ZIP/Postal" ->
 * "zip postal"; "Lat." -> "lat".
 */
export function normalizeHeader(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[_\-/.\\|]+/g, " ")
    .replace(/[^a-z0-9\s]+/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Roles the matcher can assign from a header alone. `ignore` is never matched
 * by header (it's the fallback), and `label` is matched but is a soft role
 * (name-like columns) rather than a geocoding component.
 */
export type MatchableRole = Exclude<ColumnRole, "ignore">;

/**
 * Known header phrases per role (already lowercase; run through
 * `normalizeHeader` at match time so multi-word phrases compare cleanly).
 * Ordering within a role is not significant. Extend here to cover new exports.
 */
export const DICTIONARY: Record<MatchableRole, string[]> = {
  address_full: [
    "address", "full address", "street address", "mailing address",
    "property address", "site address", "location address", "addr",
    "complete address", "formatted address", "address line", "full addr",
  ],
  street: [
    "street", "street 1", "street1", "address line 1", "address 1", "addr 1",
    "addr1", "street name", "road", "street address 1", "line 1",
  ],
  city: ["city", "town", "municipality", "locality", "city name", "place name city"],
  state: [
    "state", "province", "region", "st", "state province", "state code",
    "province state", "admin1", "state name",
  ],
  zip: [
    "zip", "zip code", "zipcode", "postal", "postal code", "postcode",
    "zip 4", "zip4", "postalcode", "post code", "zip postal",
  ],
  country: ["country", "nation", "country code", "ctry", "country name", "iso country"],
  lat: [
    "lat", "latitude", "y", "y coord", "y coordinate", "lat dd", "latdd",
    "lat deg", "gps lat", "latitude dd",
  ],
  lng: [
    "lng", "lon", "long", "longitude", "x", "x coord", "x coordinate",
    "lon dd", "lng dd", "gps lng", "gps lon", "longitude dd",
  ],
  label: [
    "name", "title", "label", "business name", "company", "company name",
    "place", "location name", "store", "store name", "branch", "office",
    "customer", "customer name", "account", "account name", "location",
    "site", "venue", "description",
  ],
};

/** All roles that participate in header matching, in a stable order. */
export const MATCHABLE_ROLES: MatchableRole[] = Object.keys(DICTIONARY) as MatchableRole[];

/**
 * Exact-alias lookup: the normalized header equals a known phrase for exactly
 * one role. Returns that role, or null when ambiguous/unknown (the fuzzy layer
 * handles those). Deterministic and cheap; the common "Address"/"City"/"Zip"
 * headers resolve here with no distance math.
 */
export function exactRole(header: string): MatchableRole | null {
  const norm = normalizeHeader(header);
  if (norm === "") return null;
  const hits = MATCHABLE_ROLES.filter((role) => DICTIONARY[role].includes(norm));
  return hits.length === 1 ? hits[0]! : null;
}
